<script lang="ts">
  import ProblemNotice from '../lib/ProblemNotice.svelte'
  import { appProblem } from '../lib/problems'
  import { LANGUAGES, t } from '../lib/i18n.svelte'
  import { onMount } from 'svelte'
  import Brand from '../lib/Brand.svelte'
  import { navigate } from '../lib/router.svelte'
  import { dev, setDevMode } from '../lib/dev.svelte'
  import { applyAppearance, prefs, savePrefs, type Appearance } from '../lib/prefs.svelte'
  import {
    appInfo,
    encoderInfo,
    errorMessage,
    setOverlayHotkey,
    setOverlaySide,
    type AppInfo,
  } from '../lib/tauri'
  import { comboFromEvent } from '../lib/hotkey'
  import { isDesktopApp, isMobileBuild } from '../lib/platform'
  import { checkNow, installNow, update } from '../lib/update.svelte'
  import { account } from '../lib/account.svelte'
  import { friends } from '../lib/friends.svelte'
  import HandleField from '../lib/HandleField.svelte'
  import { tone } from '../lib/social'
  import FriendsRail from '../lib/FriendsRail.svelte'

  const me = $derived(friends.me)
  let nameDraft = $state('')
  let nameFor = ''
  let editingHandle = $state(false)
  let handleDraft = $state('')
  let handleOk = $state(false)
  let profileError = $state<string | null>(null)
  let savingProfile = $state(false)

  // Follow the account's name until the person starts typing a different one.
  $effect(() => {
    if (me && nameFor !== me.name) {
      nameDraft = me.name
      nameFor = me.name
    }
  })

  const handleLockedUntil = $derived(
    me && me.handleChangeAfter * 1000 > Date.now() ? me.handleChangeAfter * 1000 : 0,
  )

  async function saveProfile(name: string, handle: string | null): Promise<boolean> {
    savingProfile = true
    profileError = null
    try {
      await friends.setProfile(name, handle)
      return true
    } catch (caught) {
      profileError = caught instanceof Error ? t(caught.message) : String(caught)
      return false
    } finally {
      savingProfile = false
    }
  }

  async function saveName() {
    const name = nameDraft.trim()
    if (!me || !name || name === me.name) return
    await saveProfile(name, null)
  }

  async function saveHandle() {
    if (!handleOk) return
    if (await saveProfile(nameDraft.trim() || me?.name || '', handleDraft)) editingHandle = false
  }

  let capturing = $state(false)
  let hotkeyError = $state<string | null>(null)

  async function captureKey(event: KeyboardEvent) {
    if (!capturing) return
    event.preventDefault()
    if (event.key === 'Escape') {
      capturing = false
      return
    }
    if (event.ctrlKey && event.altKey) {
      hotkeyError = t('Ctrl+Alt types characters on many keyboards. Pick another combination.')
      return
    }
    const combo = comboFromEvent(event)
    if (!combo) return
    capturing = false
    try {
      await setOverlayHotkey(combo)
      prefs.chatHotkey = combo
      savePrefs()
      hotkeyError = null
    } catch (err) {
      hotkeyError = errorMessage(err)
    }
  }

  let editingProfile = $state(false)
  let deleteConfirmation = $state('')
  const showRail = $derived(isDesktopApp && account.signedIn)

  let info = $state<AppInfo | null>(null)
  let amf = $state<boolean | null>(null)

  onMount(async () => {
    if (!isDesktopApp) return
    try {
      amf = (await encoderInfo()).amf_available
      info = await appInfo()
    } catch {
      // The version line is decoration; settings work without it.
    }
  })

  // Five quick clicks on the version line unlock (or relock) developer mode.
  let versionClicks = 0
  let versionTimer: ReturnType<typeof setTimeout> | undefined
  function versionClick() {
    versionClicks += 1
    clearTimeout(versionTimer)
    versionTimer = setTimeout(() => (versionClicks = 0), 1500)
    if (versionClicks >= 5) {
      versionClicks = 0
      setDevMode(!prefs.devMode)
    }
  }

  function setAppearance(value: Appearance) {
    prefs.appearance = value
    applyAppearance()
    savePrefs()
  }

  const appearances: [Appearance, string][] = [
    ['light', 'Light'],
    ['dark', 'Dark'],
    ['system', isMobileBuild ? 'System' : 'Windows'],
  ]
</script>

{#snippet toggleCard(title: string, text: string, key: 'miniOnLive' | 'chatOverlay' | 'showStats')}
  <div class="golive-card">
    <div class="spread">
      <strong>{title}</strong>
      <label class="toggle">
        <input type="checkbox" bind:checked={prefs[key]} onchange={savePrefs} aria-label={title} />
      </label>
    </div>
    <p>{text}</p>
    <span class="golive-card__state" class:golive-card__state--on={prefs[key]}
      >{prefs[key] ? t('On') : t('Off')}</span
    >
  </div>
{/snippet}

<div class="settings-split">
  {#if showRail}
    <FriendsRail active="settings" />
  {/if}
  <div class="settings-scroll">
    <div class="settings-main">
      {#if !showRail}
        <div class="row" style="flex-wrap: nowrap">
          <button
            type="button"
            class="round-button"
            aria-label={t('Back')}
            onclick={() => navigate('/')}
          >
            <svg viewBox="0 -960 960 960" aria-hidden="true"
              ><path
                d="m274-450 227 227q9 9 9 21t-9 21q-9 9-21 9t-21-9L181-459q-5-5-7-10t-2-11q0-6 2-11t7-10l278-278q9-9 21-9t21 9q9 9 9 21t-9 21L274-510h496q13 0 21.5 8.5T800-480q0 13-8.5 21.5T770-450H274Z"
              /></svg
            >
          </button>
          <h1 style="margin: 0; font-size: 2.25rem; letter-spacing: -0.03em">{t('Settings')}</h1>
        </div>
      {/if}

      {#if me}
        <section class="profile-hero">
          <span class="avatar profile-hero__avatar avatar--{tone(me.name)}"
            >{(me.name[0] ?? '?').toUpperCase()}</span
          >
          <div class="profile-hero__text">
            <h2>{me.name}</h2>
            <p>
              {me.handle ? `@${me.handle}` : t('No handle yet')}{account.provider
                ? ` · ${account.provider}`
                : ''}{handleLockedUntil
                ? ` · ${t('handle changeable on {date}', {
                    date: new Date(handleLockedUntil).toLocaleDateString(),
                  })}`
                : ''}
            </p>
          </div>
          <div class="profile-hero__actions">
            <button
              type="button"
              class="profile-hero__edit"
              onclick={() => (editingProfile = !editingProfile)}
              >{editingProfile ? t('Done') : t('Edit profile')}</button
            >
            <button type="button" class="profile-hero__out" onclick={() => account.signOut()}
              >{t('Sign out')}</button
            >
          </div>
        </section>
        {#if editingProfile}
          <div class="settings-group">
            <div class="settings-row">
              <div class="settings-row__text">
                <span>{t('Display name')}</span>
                <span class="faint">{t('Shown to friends and in rooms')}</span>
              </div>
              <input
                type="text"
                bind:value={nameDraft}
                onchange={saveName}
                maxlength="32"
                aria-label={t('Display name')}
                disabled={savingProfile}
              />
            </div>
            <div class="settings-row" style="align-items: flex-start">
              <div class="settings-row__text">
                <span>{t('Handle')}</span>
                <span class="faint">
                  {#if handleLockedUntil}
                    {t('You can change it again on {date}', {
                      date: new Date(handleLockedUntil).toLocaleDateString(),
                    })}
                  {:else}
                    {t('How friends find you')}
                  {/if}
                </span>
              </div>
              {#if editingHandle}
                <div class="stack" style="gap: 8px; min-width: min(100%, 260px)">
                  <HandleField
                    bind:value={handleDraft}
                    bind:ok={handleOk}
                    current={me.handle}
                    id="settings-handle"
                  />
                  <div class="row">
                    <button
                      type="button"
                      class="primary"
                      disabled={!handleOk || savingProfile}
                      onclick={saveHandle}>{t('Save')}</button
                    >
                    <button type="button" class="ghost" onclick={() => (editingHandle = false)}
                      >{t('Cancel')}</button
                    >
                  </div>
                </div>
              {:else}
                <button
                  type="button"
                  disabled={friends.status !== 'online' || handleLockedUntil > 0}
                  onclick={() => {
                    handleDraft = me.handle ?? ''
                    profileError = null
                    editingHandle = true
                  }}>{me.handle ? t('Change') : t('Choose')}</button
                >
              {/if}
            </div>
          </div>
        {/if}
        {#if profileError}<ProblemNotice
            problem={appProblem(profileError, 'account')}
            compact
          />{/if}
      {:else}
        <div class="settings-group">
          <div class="settings-row">
            <div class="settings-row__text">
              <span>{t('Your name')}</span>
              <span class="faint">{t('Shown to friends in chat')}</span>
            </div>
            <input
              type="text"
              bind:value={prefs.displayName}
              onchange={savePrefs}
              placeholder="Host"
              maxlength="32"
              aria-label={t('Your name')}
            />
          </div>
        </div>
      {/if}

      {#if !isMobileBuild}
        <section>
          <div class="settings-head">
            <h2>{t('When you go live')}</h2>
            <span>{t('Applies from the next stream')}</span>
          </div>
          <div class="golive-grid">
            {@render toggleCard(
              t('Mini view'),
              t('A small always-on-top window with viewers and chat.'),
              'miniOnLive',
            )}
            <div class="golive-card golive-card--encoder">
              <div class="golive-card__heading">
                <strong>{t('Chat overlay side')}</strong>
                <select
                  class="golive-card__select"
                  bind:value={prefs.chatSide}
                  onchange={() => {
                    savePrefs()
                    void setOverlaySide(prefs.chatSide).catch(() => {})
                  }}
                  aria-label={t('Chat overlay side')}
                >
                  <option value="left">{t('Left')}</option>
                  <option value="right">{t('Right')}</option>
                </select>
              </div>
              <p>{t('Where chat sits over the game and over the video you watch.')}</p>
            </div>
            <div class="golive-card golive-card--encoder">
              <div class="golive-card__heading">
                <strong>{t('Typing key')}</strong>
                <button
                  type="button"
                  class="ghost"
                  aria-label={t('Typing key')}
                  onclick={() => {
                    capturing = true
                    hotkeyError = null
                  }}
                  onkeydown={captureKey}
                  onblur={() => (capturing = false)}
                >
                  {capturing ? t('Press the keys…') : prefs.chatHotkey}
                </button>
              </div>
              {#if hotkeyError}
                <ProblemNotice problem={appProblem(hotkeyError, 'settings')} compact />
              {:else}
                <p>{t('Press this key in your game to type in the chat overlay.')}</p>
              {/if}
            </div>
            {@render toggleCard(
              t('Chat overlay'),
              t('Translucent chat over your game, hidden from the stream.'),
              'chatOverlay',
            )}
            {@render toggleCard(
              t('Technical stats'),
              t('fps, bitrate and encode time over the preview.'),
              'showStats',
            )}
            {#if isDesktopApp}
              <div class="golive-card golive-card--encoder">
                <div class="golive-card__heading">
                  <strong>{t('Video encoder')}</strong>
                  <select
                    class="golive-card__select"
                    bind:value={prefs.encoder}
                    onchange={savePrefs}
                    aria-label={t('Video encoder')}
                  >
                    <option value="auto">{t('Auto')}</option>
                    <option value="amf" disabled={amf === false}>AMD AMF</option>
                    <option value="mf">{t('Media Foundation')}</option>
                  </select>
                </div>
                <p>{t('Picked for you. Change only if streams stutter.')}</p>
                <span class="golive-card__state golive-card__state--ok">
                  ● {prefs.encoder === 'amf' || (prefs.encoder === 'auto' && amf)
                    ? 'AMD AMF'
                    : t('Media Foundation')}
                  · {amf ? t('using your GPU') : t('software fallback')}
                </span>
              </div>
            {/if}
          </div>
        </section>
      {/if}

      <section>
        <div class="settings-head"><h2>{t('App')}</h2></div>
        <div class="app-card">
          <div style="flex: 1">
            <div class="eyebrow" style="padding: 0">{t('Language')}</div>
            <select
              class="app-card__select"
              bind:value={prefs.language}
              onchange={savePrefs}
              aria-label={t('Language')}
            >
              {#each LANGUAGES as language (language.value)}
                <option value={language.value}>{language.label}</option>
              {/each}
            </select>
            <div class="faint" style="margin-top: 6px; font-size: 12px">
              {t('Also the page friends see')}
            </div>
          </div>
          <div>
            <div class="eyebrow" style="padding: 0 0 6px">{t('Appearance')}</div>
            <div class="swatches" role="group" aria-label={t('Appearance')}>
              {#each appearances as [value, label] (value)}
                <button
                  type="button"
                  class="swatch swatch--{value}"
                  aria-pressed={prefs.appearance === value}
                  onclick={() => setAppearance(value)}
                >
                  <span class="swatch__chip"><i></i><i></i></span>
                  {t(label)}
                </button>
              {/each}
            </div>
          </div>
        </div>
      </section>

      {#if info}
        <div class="row" style="gap: 12px; font-size: 0.8125rem; flex-wrap: wrap">
          {#if update.available}
            <span>{t('Sofa {version} is ready.', { version: update.available.version })}</span>
            <button type="button" class="primary" disabled={update.installing} onclick={installNow}>
              {update.installing ? t('Updating…') : t('Update and restart')}
            </button>
          {:else}
            <button
              type="button"
              class="ghost"
              disabled={update.checking}
              onclick={() => checkNow()}
            >
              {update.checking ? t('Checking…') : t('Check for updates')}
            </button>
            {#if update.upToDate}<span style="color: var(--text-dim)"
                >{t('You are up to date.')}</span
              >{/if}
          {/if}
          {#if update.error}<ProblemNotice
              problem={appProblem(update.error, 'update')}
              compact
            />{/if}
        </div>
      {/if}

      {#if me}
        <details class="danger-zone">
          <summary>{t('Delete account…')}</summary>
          <p>
            {t('Permanently delete your account, friends and requests. Type DELETE to confirm.')}
          </p>
          <div class="row">
            <input
              bind:value={deleteConfirmation}
              aria-label={t('Type DELETE to confirm')}
              autocomplete="off"
            />
            <button
              type="button"
              disabled={deleteConfirmation !== 'DELETE' || friends.status !== 'online'}
              onclick={() => {
                friends.deleteAccount()
                deleteConfirmation = ''
              }}>{t('Delete account permanently')}</button
            >
          </div>
        </details>
      {/if}

      <div
        class="row"
        style="gap: 12px; color: var(--text-dim); font-size: 0.8125rem; flex-wrap: nowrap"
      >
        <Brand size={20} wordmark={false} />
        <span>
          <!-- svelte-ignore a11y_no_noninteractive_element_interactions, a11y_click_events_have_key_events -->
          <strong style="color: var(--text)" onclick={versionClick}
            >Sofa {info?.version ?? ''}{dev.on ? ' · dev' : ''}</strong
          >
          {#if info}· {t('protocol')} {info.protocol_version}{/if}
          · {t('Accounts are optional. P2P video goes straight from this PC to your friends.')}
        </span>
      </div>
    </div>
  </div>
</div>
